/**
 * Kanban - Página principal do sistema Kanban de conversas
 *
 * Permite organizar conversas em categorias e fases,
 * com suporte a drag & drop para mover conversas entre fases
 *
 * RELEASE: v2.0.0 - Header padronizado igual ao Kanban de Negócios
 *  (filtros compactos h-[22px], busca ghost, separator vertical, ações icon-sm à direita)
 */

import { useState, useMemo, useCallback } from 'react';
import { Kanban as KanbanIcon, RefreshCw, Inbox, MessageSquare, Instagram, MessagesSquare, Send, Globe, Users, Mail, MailOpen, Eye, Building2, ChevronDown, Search, X, Zap, TrendingUp, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useConversationPhaseAutomations } from '@/hooks/useConversationPhaseAutomations';
import { ConversationAutomationModal } from '@/components/tenant/kanban/ConversationAutomationModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useKanban } from '@/hooks/useKanban';
import { useKanbanConversationCrmRealtime } from '@/hooks/useKanbanConversationCrmRealtime';
import { KanbanBoard } from '@/components/tenant/kanban/KanbanBoard';
import { KanbanCategoryTabs } from '@/components/tenant/kanban/KanbanCategoryTabs';
import { Separator } from '@/components/ui/separator';
import { KanbanEmptyState } from '@/components/tenant/kanban/KanbanEmptyState';
import { cn } from '@/lib/utils';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { KanbanCategorySelect } from '@/components/kanban/KanbanCategorySelect';

/**
 * Retorna ícone do canal de comunicação
 * Mantém padrão visual consistente com a tela de Chat
 */
const getChannelIcon = (channelType: string) => {
  switch (channelType) {
    case 'whatsapp':
    case 'whatsapp_oficial':
      return <MessageSquare className="h-3.5 w-3.5 text-green-600" />;
    case 'instagram':
      return <Instagram className="h-3.5 w-3.5 text-pink-500" />;
    case 'messenger':
      return <MessagesSquare className="h-3.5 w-3.5 text-blue-500" />;
    case 'telegram':
      return <Send className="h-3.5 w-3.5 text-sky-500" />;
    case 'webchat':
      return <Globe className="h-3.5 w-3.5 text-muted-foreground" />;
    default:
      return <Inbox className="h-3.5 w-3.5 text-muted-foreground" />;
  }
};

export default function TenantKanban() {
  // Estado para controlar a expansão do campo de busca
  // v1.1.0: busca começa recolhida; expande somente ao clicar na lupa
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // Estados de abertura dos popovers de filtros (busca + scroll interno)
  const [inboxPopoverOpen, setInboxPopoverOpen] = useState(false);
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  const [deptPopoverOpen, setDeptPopoverOpen] = useState(false);
  
  // Estado do modal de automações
  const [automationModalOpen, setAutomationModalOpen] = useState(false);

  // Toggle CRM: persiste preferência em localStorage
  // Estado do toggle CRM - padrão é LIGADO (true) caso não haja preferência salva
  const [showCrmData, setShowCrmData] = useState(() => {
    try {
      const saved = localStorage.getItem('kanban-conv-crm-toggle');
      // Se nunca foi salvo, retorna true (padrão ligado)
      return saved === null ? true : saved === 'true';
    } catch { return false; }
  });
  const handleToggleCrm = useCallback((checked: boolean) => {
    setShowCrmData(checked);
    try { localStorage.setItem('kanban-conv-crm-toggle', String(checked)); } catch {}
  }, []);

  // Hook de autenticação para verificar crm_enabled
  const { currentTenant } = useAuth();
  const crmEnabled = currentTenant?.crm_enabled ?? false;

  // Realtime: escuta mudanças em contracts e crm_deals para atualizar cards CRM
  useKanbanConversationCrmRealtime(currentTenant?.id, crmEnabled && showCrmData);

  // Hook de permissões para controlar visibilidade de automações e ações estruturais
  const { isAdmin, canView, canCreate, canEdit, canDelete } = usePermissions();
  
  // Calcula se usuário pode ver os ícones de automação
  // Admin: sempre | Supervisor: se tiver acesso a flows ou routing | Agent: nunca
  const canViewAutomations = useMemo(() => {
    if (isAdmin) return true;
    return canView('flows') || canView('routing');
  }, [isAdmin, canView]);

  // v2.1.0: Permissões granulares do menu 'kanban' aplicadas à UI estrutural
  // (criar/editar/excluir fases e categorias). O backend (RPC + RLS) já bloqueia,
  // estas flags servem apenas para esconder os botões de quem não pode usá-los.
  const canCreateKanban = canCreate('kanban');
  const canEditKanban = canEdit('kanban');
  const canDeleteKanban = canDelete('kanban');

  const {
    categories,
    phases,
    selectedCategoryId,
    loading,
    loadingPhases,
    inboxes,
    inboxFilter,
    setInboxFilter,
    // Filtros
    agents,
    agentFilter,
    setAgentFilter,
    readStatusFilter,
    setReadStatusFilter,
    departments,
    departmentFilter,
    setDepartmentFilter,
    // Busca por nome, telefone ou email
    searchTerm,
    setSearchTerm,
    // Ações
    setSelectedCategoryId,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories, // v2.2.0: reordenação das abas por drag and drop

    createPhase,
    updatePhase,
    deletePhase,
    moveConversation,
    refresh,
  } = useKanban();

  // Preferência do usuário: formato de visualização das categorias (tabs | select)
  // IMPORTANTE: mantido no topo (antes dos early returns) para respeitar Rules of Hooks
  const { data: userPrefs } = useUserPreferences();
  const viewMode = userPrefs?.kanban_view_conversations ?? 'tabs';

  // Hook de automações (para exibir contador no botão e passar para o Board)
  const { 
    totalAutomations, 
    hasAutomation, 
    automationCount,
  } = useConversationPhaseAutomations(selectedCategoryId);

  // Labels dinâmicos para filtros
  const inboxLabel = inboxFilter === 'all' 
    ? 'Inbox' 
    : inboxes.find(i => i.id === inboxFilter)?.name || 'Inbox';
  
  const agentLabel = agentFilter === 'all' 
    ? 'Agente' 
    : agents.find(a => a.id === agentFilter)?.full_name || 'Agente';
  
  const readStatusLabel = readStatusFilter === 'all' 
    ? 'Leitura' 
    : readStatusFilter === 'read' ? 'Lidas' : 'Não lidas';
  
  const deptLabel = departmentFilter === 'all' 
    ? 'Depto' 
    : departments.find(d => d.id === departmentFilter)?.name || 'Depto';

  // Loading inicial
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="flex gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[400px] w-72" />
          ))}
        </div>
      </div>
    );
  }

  // Estado vazio - sem categorias
  if (categories.length === 0) {
    return (
      <div className="p-6">
        <KanbanEmptyState onCreateCategory={createCategory} />
      </div>
    );
  }




  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border/50 bg-background">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-3 mr-2 shrink-0">
            {viewMode === 'select' ? (
              // Modo seleção: dropdown de categoria substitui o título
              <KanbanCategorySelect
                icon={KanbanIcon}
                categories={categories}
                selectedId={selectedCategoryId}
                onChange={setSelectedCategoryId}
              />
            ) : (
              <>
                <KanbanIcon className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-semibold">Kanban de Conversas</h1>
              </>
            )}
          </div>

          {/* Busca expansível (estilo ghost — padrão Deal Kanban) */}
          <div className="flex items-center shrink-0">
            <div
              className={`flex items-center overflow-hidden transition-all duration-300 ease-out ${
                isSearchExpanded ? 'w-44 sm:w-64 opacity-100' : 'w-0 opacity-0'
              }`}
            >
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-7 w-full text-xs rounded-full pl-3 pr-2 sm:min-w-[250px]"
                autoFocus={isSearchExpanded}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full flex-shrink-0 transition-colors duration-200"
              onClick={() => {
                if (isSearchExpanded) {
                  setSearchTerm('');
                }
                setIsSearchExpanded(!isSearchExpanded);
              }}
              title={isSearchExpanded ? 'Fechar busca' : 'Buscar por nome, telefone ou email'}
            >
              {isSearchExpanded ? (
                <X className="h-3.5 w-3.5" />
              ) : (
                <Search className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          {/* Separador vertical entre busca e filtros (oculto em mobile) */}
          <Separator orientation="vertical" className="h-5 hidden sm:block shrink-0" />

          {/* Grupo de filtros rápidos compactos (h-[22px]) — padrão Activities/Deal */}
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide min-w-0">
            {/* Filtro por Inbox - Popover + Command (busca + scroll interno) */}
            <Popover open={inboxPopoverOpen} onOpenChange={setInboxPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-[22px] px-2 text-xs gap-1.5 shrink-0",
                    inboxFilter !== 'all' && "border-primary bg-primary/5"
                  )}
                >
                  <Inbox className="h-3 w-3" />
                  <span className="max-w-[80px] truncate">{inboxLabel}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar inbox..." />
                  <CommandList>
                    <CommandEmpty>Nenhuma inbox encontrada.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__"
                        onSelect={() => { setInboxFilter('all'); setInboxPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          inboxFilter === 'all' ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {inboxFilter === 'all' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <Inbox className="h-4 w-4 mr-2 opacity-50" />
                        <span>Todas as Inboxes</span>
                      </CommandItem>
                    </CommandGroup>
                    {inboxes.length > 0 && <CommandSeparator />}
                    <CommandGroup heading="Inboxes">
                      {inboxes.map((inbox) => (
                        <CommandItem
                          key={inbox.id}
                          value={inbox.name}
                          onSelect={() => { setInboxFilter(inbox.id); setInboxPopoverOpen(false); }}
                          className="cursor-pointer"
                        >
                          <div className={cn(
                            "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                            inboxFilter === inbox.id ? "border-primary bg-primary" : "border-muted"
                          )}>
                            {inboxFilter === inbox.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                          </div>
                          <span className="mr-2 inline-flex">{getChannelIcon(inbox.channel_type)}</span>
                          <span className="truncate">{inbox.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Filtro por Agente - Popover + Command com avatares */}
            <Popover open={agentPopoverOpen} onOpenChange={setAgentPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-[22px] px-2 text-xs gap-1.5 shrink-0",
                    agentFilter !== 'all' && "border-primary bg-primary/5"
                  )}
                >
                  <Users className="h-3 w-3" />
                  <span className="max-w-[80px] truncate">{agentLabel}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar agente..." />
                  <CommandList>
                    <CommandEmpty>Nenhum agente encontrado.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__"
                        onSelect={() => { setAgentFilter('all'); setAgentPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          agentFilter === 'all' ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {agentFilter === 'all' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <Users className="h-4 w-4 mr-2 opacity-50" />
                        <span>Todos Agentes</span>
                      </CommandItem>
                    </CommandGroup>
                    {agents.length > 0 && <CommandSeparator />}
                    <CommandGroup heading="Agentes">
                      {agents.map((agent) => (
                        <CommandItem
                          key={agent.id}
                          value={agent.full_name || agent.id}
                          onSelect={() => { setAgentFilter(agent.id); setAgentPopoverOpen(false); }}
                          className="cursor-pointer"
                        >
                          <div className={cn(
                            "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                            agentFilter === agent.id ? "border-primary bg-primary" : "border-muted"
                          )}>
                            {agentFilter === agent.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                          </div>
                          <SmartAvatar
                            src={agent.avatar_url}
                            name={agent.full_name || 'Agente'}
                            size="sm"
                            use3DFallback
                            className="mr-2"
                          />
                          <span className="truncate">{agent.full_name || 'Sem nome'}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Filtro por Status de Leitura */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-[22px] px-2 text-xs gap-1.5 shrink-0">
                  {readStatusFilter === 'unread' ? <Eye className="h-3 w-3" /> :
                   readStatusFilter === 'read' ? <MailOpen className="h-3 w-3" /> :
                   <Mail className="h-3 w-3" />}
                  <span>{readStatusLabel}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-popover">
                <DropdownMenuRadioGroup value={readStatusFilter} onValueChange={(v) => setReadStatusFilter(v as 'all' | 'read' | 'unread')}>
                  <DropdownMenuRadioItem value="all" className="gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Todas
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="read" className="gap-2">
                    <MailOpen className="h-4 w-4 text-green-600" />
                    Lidas
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="unread" className="gap-2">
                    <Eye className="h-4 w-4 text-orange-500" />
                    Não lidas
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Filtro por Departamento - Popover + Command (busca + scroll interno) */}
            <Popover open={deptPopoverOpen} onOpenChange={setDeptPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-[22px] px-2 text-xs gap-1.5 shrink-0",
                    departmentFilter !== 'all' && "border-primary bg-primary/5"
                  )}
                >
                  <Building2 className="h-3 w-3" />
                  <span className="max-w-[80px] truncate">{deptLabel}</span>
                  <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar departamento..." />
                  <CommandList>
                    <CommandEmpty>Nenhum departamento encontrado.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__"
                        onSelect={() => { setDepartmentFilter('all'); setDeptPopoverOpen(false); }}
                        className="cursor-pointer"
                      >
                        <div className={cn(
                          "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                          departmentFilter === 'all' ? "border-primary bg-primary" : "border-muted"
                        )}>
                          {departmentFilter === 'all' && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                        </div>
                        <Building2 className="h-4 w-4 mr-2 opacity-50" />
                        <span>Todos Departamentos</span>
                      </CommandItem>
                    </CommandGroup>
                    {departments.length > 0 && <CommandSeparator />}
                    <CommandGroup heading="Departamentos">
                      {departments.map((dept) => (
                        <CommandItem
                          key={dept.id}
                          value={dept.name}
                          onSelect={() => { setDepartmentFilter(dept.id); setDeptPopoverOpen(false); }}
                          className="cursor-pointer"
                        >
                          <div className={cn(
                            "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                            departmentFilter === dept.id ? "border-primary bg-primary" : "border-muted"
                          )}>
                            {departmentFilter === dept.id && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                          </div>
                          <Building2 className="h-4 w-4 mr-2 text-primary" />
                          <span className="truncate">{dept.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Spacer empurra ações para a direita */}
          <div className="flex-1 min-w-0" />

          {/* Grupo de ações à direita (icon-sm, padrão Deal Kanban) */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Atualizar */}
            <Button
              variant="outline"
              size="icon-sm"
              onClick={refresh}
              title="Atualizar dados"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>

            {/* Automações (somente admins/supervisores com permissão) */}
            {canViewAutomations && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setAutomationModalOpen(true)}
                  title="Regras de Automação"
                >
                  <Zap className={`h-3.5 w-3.5 ${totalAutomations > 0 ? 'text-amber-500' : ''}`} />
                </Button>
                {totalAutomations > 0 && (
                  <Badge
                    variant="secondary"
                    className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] pointer-events-none"
                  >
                    {totalAutomations}
                  </Badge>
                )}
              </div>
            )}

            {/* Toggle CRM (apenas com módulo habilitado) */}
            {crmEnabled && (
              <div className="flex items-center gap-1.5 pl-2">
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">CRM</span>
                <Switch
                  checked={showCrmData}
                  onCheckedChange={handleToggleCrm}
                  className="h-4 w-8 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input"
                />
              </div>
            )}
          </div>
        </div>

        {/* Tabs de Categorias — apenas no modo 'tabs' (padrão).
            No modo 'select' a categoria é escolhida via dropdown no header. */}
        {viewMode === 'tabs' && (
          <KanbanCategoryTabs
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={setSelectedCategoryId}
            onCreateCategory={createCategory}
            onUpdateCategory={updateCategory}
            onDeleteCategory={deleteCategory}
            canCreateCategory={canCreateKanban}
            canEditCategory={canEditKanban}
            canDeleteCategory={canDeleteKanban}
            onReorderCategories={reorderCategories}
          />

        )}
      </div>

      {/* Board */}
      <div className="flex-1 overflow-hidden bg-muted/20">
        {loadingPhases ? (
          <div className="flex gap-4 p-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[400px] w-72" />
            ))}
          </div>
        ) : selectedCategoryId ? (
          <KanbanBoard
            phases={phases}
            categoryId={selectedCategoryId}
            filters={{
              inboxId: inboxFilter !== 'all' ? inboxFilter : null,
              agentId: agentFilter !== 'all' ? agentFilter : null,
              departmentId: departmentFilter !== 'all' ? departmentFilter : null,
              readStatus: readStatusFilter !== 'all' ? readStatusFilter : null,
              search: searchTerm.trim() || null,
            }}
            showCrmInfo={crmEnabled && showCrmData}
            onCreatePhase={createPhase}
            onUpdatePhase={updatePhase}
            onDeletePhase={deletePhase}
            onMoveConversation={moveConversation}
            onRefresh={refresh}
            // Permissões granulares (menu 'kanban') aplicadas à UI estrutural
            canCreatePhase={canCreateKanban}
            canEditPhase={canEditKanban}
            canDeletePhase={canDeleteKanban}
            // Props de automação
            canViewAutomations={canViewAutomations}
            hasAutomation={hasAutomation}
            automationCount={automationCount}
            onAutomationClick={() => setAutomationModalOpen(true)}
          />
        ) : null}
      </div>

      {/* Modal de Automações - Visível apenas para admins/supervisores com permissão */}
      {canViewAutomations && (
        <ConversationAutomationModal
          open={automationModalOpen}
          onOpenChange={setAutomationModalOpen}
          categoryId={selectedCategoryId}
          categoryName={categories.find(c => c.id === selectedCategoryId)?.name || 'Categoria'}
          phases={phases}
        />
      )}
    </div>
  );
}